/**
 * InAppCallProvider — global voice-call manager for the dashboard.
 *
 * Registers this browser as a Twilio Voice Client (per-person identity),
 * heartbeats so the backend knows the device is genuinely live, exposes
 * mic-permission + availability state, and screen-pop info for incoming
 * calls. Designed to be a no-op when no number routes in-app.
 */
import React, {
  createContext, useContext, useEffect, useMemo, useRef, useState, useCallback,
} from "react";
import axios from "axios";
import { Device } from "@twilio/voice-sdk";
import { API } from "../App";

const InAppCallContext = createContext({
  status: "idle",            // idle | registering | ready | permission_required | error
  enabled: false,
  active: false,
  incoming: null,
  incomingInfo: null,
  outgoing: null,
  micPermission: "unknown",
  availability: "available",
  lastRegisteredAt: null,
  setAvailability: () => {},
  testCall: async () => {},
  registerThisDevice: () => {},
  unregisterThisDevice: () => {},
  startOutboundCall: async () => {},
  acceptIncoming: () => {},
  rejectIncoming: () => {},
  hangup: () => {},
  toggleMute: () => {},
  isMuted: false,
});

export const useInAppCall = () => useContext(InAppCallContext);

const getAuthConfigFromLS = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("tech_token");
  if (!t) return null;
  return { headers: { Authorization: `Bearer ${t}` } };
};

export const InAppCallProvider = ({ children }) => {
  const [status, setStatus] = useState("idle");
  const [enabled, setEnabled] = useState(false);
  const [incoming, setIncoming] = useState(null);
  const [incomingInfo, setIncomingInfo] = useState(null);
  const [outgoing, setOutgoing] = useState(null);
  const [isMuted, setMuted] = useState(false);
  const [micPermission, setMicPermission] = useState("unknown");
  const [availability, setAvailabilityState] = useState("available");
  const [lastRegisteredAt, setLastRegisteredAt] = useState(null);
  const deviceRef = useRef(null);
  const registeredClientIdRef = useRef(null);
  const heartbeatRef = useRef(null);

  /* ── 1) Bootstrap: read settings to know whether to register ── */
  useEffect(() => {
    let cancelled = false;
    const auth = getAuthConfigFromLS();
    if (!auth) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/phone/call-routing/settings`, auth);
        if (cancelled) return;
        setEnabled(!!res.data?.call_routing_enabled);
      } catch { /* silently no-op */ }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ── Availability bootstrap ── */
  useEffect(() => {
    if (!enabled) return;
    const auth = getAuthConfigFromLS();
    if (!auth) return;
    axios.get(`${API}/phone/voice-client/availability`, auth)
      .then((r) => setAvailabilityState(r.data?.status || "available"))
      .catch(() => {});
  }, [enabled]);

  const setAvailability = useCallback(async (s) => {
    const auth = getAuthConfigFromLS();
    if (!auth) return;
    setAvailabilityState(s);
    axios.put(`${API}/phone/voice-client/availability`, { status: s }, auth).catch(() => {});
  }, []);

  /* ── Helpers ── */

  const fetchToken = useCallback(async () => {
    const auth = getAuthConfigFromLS();
    if (!auth) throw new Error("not authenticated");
    const res = await axios.post(`${API}/phone/voice-client/access-token`, {}, auth);
    return res.data;
  }, []);

  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(() => {
      const cid = registeredClientIdRef.current;
      const a = getAuthConfigFromLS();
      if (cid && a) {
        axios.post(`${API}/phone/voice-client/heartbeat`, { client_id: cid }, a).catch(() => {});
      }
    }, 60000);
  }, []);

  const registerThisDevice = useCallback(async () => {
    const auth = getAuthConfigFromLS();
    if (!auth) return;
    setStatus("registering");

    // Microphone is required to answer — request up-front so we can show an
    // honest status instead of failing silently at answer time.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicPermission("granted");
    } catch {
      setMicPermission("denied");
      setStatus("permission_required");
      return;
    }

    try {
      const { token } = await fetchToken();

      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* ignore */ }
      }
      const device = new Device(token, {
        codecPreferences: ["opus", "pcmu"],
        closeProtection: true,
        logLevel: "error",
      });
      deviceRef.current = device;

      device.on("registered", async () => {
        setStatus("ready");
        try {
          const res = await axios.post(`${API}/phone/voice-client/register`, {
            platform: "web",
            device_label: navigator.userAgent.includes("Mac") ? "Browser (Mac)" : "Browser",
          }, auth);
          registeredClientIdRef.current = res.data?.client_id;
          setLastRegisteredAt(new Date().toISOString());
          startHeartbeat();
        } catch { /* non-fatal */ }
      });
      device.on("unregistered", () => {
        // Auto-reconnect if we're supposed to be live (network blip etc.)
        setStatus((prev) => (prev === "ready" ? "registering" : prev));
        setTimeout(() => {
          try { device.register(); } catch { /* ignore */ }
        }, 3000);
      });
      device.on("error", (err) => {
        if (err?.code === 31204 || err?.code === 20104) {
          fetchToken().then(({ token: newTok }) => device.updateToken(newTok)).catch(() => {});
          return;
        }
        setStatus("error");
        // eslint-disable-next-line no-console
        console.warn("[Twilio Voice]", err);
      });
      device.on("incoming", (call) => {
        setIncoming(call);
        call.on("disconnect", () => setIncoming(null));
        call.on("cancel", () => setIncoming(null));
        call.on("reject", () => setIncoming(null));
      });
      device.on("tokenWillExpire", async () => {
        try {
          const { token: newToken } = await fetchToken();
          device.updateToken(newToken);
        } catch {
          // eslint-disable-next-line no-console
          console.error("[InAppCall] Failed to refresh voice token");
        }
      });
      await device.register();
    } catch (err) {
      setStatus("error");
      // eslint-disable-next-line no-console
      console.warn("InAppCall register failed", err);
    }
  }, [fetchToken, startHeartbeat]);

  const unregisterThisDevice = useCallback(async () => {
    const d = deviceRef.current;
    const cid = registeredClientIdRef.current;
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (d) {
      try { await d.unregister(); } catch { /* ignore */ }
      try { d.destroy(); } catch { /* ignore */ }
      deviceRef.current = null;
    }
    setStatus("idle");
    if (cid) {
      const auth = getAuthConfigFromLS();
      if (auth) {
        axios.post(`${API}/phone/voice-client/unregister`, { client_id: cid }, auth).catch(() => {});
      }
      registeredClientIdRef.current = null;
    }
  }, []);

  /* ── Auto-register when enabled flips on ── */
  useEffect(() => {
    if (enabled) {
      registerThisDevice();
    } else {
      unregisterThisDevice();
    }
    return () => { unregisterThisDevice(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  /* ── Screen-pop info for the incoming-call screen ── */
  useEffect(() => {
    if (!incoming) { setIncomingInfo(null); return; }
    const auth = getAuthConfigFromLS();
    if (!auth) return;
    const from = incoming.parameters?.From || "";
    let custom = {};
    try {
      custom = incoming.customParameters ? Object.fromEntries(incoming.customParameters) : {};
    } catch { /* ignore */ }
    const to = custom.calledNumber || incoming.parameters?.To || "";
    axios.get(`${API}/phone/voice-client/incoming-info`, {
      ...auth, params: { from_number: from, to_number: to },
    }).then((r) => {
      setIncomingInfo({
        ...r.data,
        called_number: to,
        number_label: r.data?.number_label || custom.numberLabel || "",
        location_name: r.data?.location_name || custom.locationName || "",
      });
    }).catch(() => setIncomingInfo(null));
  }, [incoming]);

  /* ── Mobile push deep-link auto-accept / auto-reject ── */
  useEffect(() => {
    if (!incoming) return;
    if (typeof window === "undefined") return;
    try {
      const url = new URL(window.location.href);
      const acceptSid = url.searchParams.get("accept_call_sid");
      const rejectSid = url.searchParams.get("reject_call_sid");
      if (acceptSid) {
        try { incoming.accept(); } catch { /* ignore */ }
        url.searchParams.delete("accept_call_sid");
        window.history.replaceState({}, "", url.toString());
      } else if (rejectSid) {
        try { incoming.reject(); } catch { /* ignore */ }
        url.searchParams.delete("reject_call_sid");
        window.history.replaceState({}, "", url.toString());
      }
    } catch { /* ignore */ }
  }, [incoming]);

  /* ── Outbound calls ── */
  const startOutboundCall = useCallback(async (toNumber) => {
    if (!deviceRef.current || status !== "ready") {
      throw new Error("Device not ready. Enable Call Routing in Phone Settings first.");
    }
    const call = await deviceRef.current.connect({ params: { To: toNumber } });
    setOutgoing(call);
    setMuted(false);
    call.on("disconnect", () => { setOutgoing(null); setMuted(false); });
    call.on("error", (e) => {
      // eslint-disable-next-line no-console
      console.warn("call err", e);
      setOutgoing(null);
    });
    return call;
  }, [status]);

  const acceptIncoming = useCallback(() => {
    if (incoming) {
      incoming.accept();
      setMuted(false);
    }
  }, [incoming]);

  const rejectIncoming = useCallback(() => {
    if (incoming) {
      incoming.reject();
      setIncoming(null);
    }
  }, [incoming]);

  const hangup = useCallback(() => {
    if (outgoing) outgoing.disconnect();
    if (incoming) {
      try { incoming.disconnect(); } catch { /* ignore */ }
      setIncoming(null);
    }
  }, [outgoing, incoming]);

  const toggleMute = useCallback(() => {
    const c = outgoing || incoming;
    if (!c) return;
    const next = !c.isMuted();
    c.mute(next);
    setMuted(next);
  }, [outgoing, incoming]);

  const testCall = useCallback(async () => {
    const auth = getAuthConfigFromLS();
    if (!auth) throw new Error("Not signed in");
    const res = await axios.post(`${API}/phone/voice-client/test-call`, {}, auth);
    return res.data;
  }, []);

  const value = useMemo(
    () => ({
      status,
      enabled,
      active: !!(outgoing || incoming),
      incoming,
      incomingInfo,
      outgoing,
      micPermission,
      availability,
      lastRegisteredAt,
      setAvailability,
      testCall,
      registerThisDevice,
      unregisterThisDevice,
      startOutboundCall,
      acceptIncoming,
      rejectIncoming,
      hangup,
      toggleMute,
      isMuted,
    }),
    [status, enabled, outgoing, incoming, incomingInfo, micPermission, availability, lastRegisteredAt,
     setAvailability, testCall, registerThisDevice, unregisterThisDevice, startOutboundCall,
     acceptIncoming, rejectIncoming, hangup, toggleMute, isMuted]
  );

  return (
    <InAppCallContext.Provider value={value}>
      {children}
    </InAppCallContext.Provider>
  );
};
